import React, { useState, useEffect, useCallback } from 'react';
import {
  Mail, ShieldCheck, Camera, CheckCircle2, User, X, ChevronRight,
  Users, Phone, Plus, ShieldAlert, FileSignature, Scale, Info,
  Search, CheckCircle, History as HistoryLog, Mic,
  MessageCircle, AlertTriangle, Scale as ScaleIcon,
  Sparkles, Loader2, FileText, Pencil, Trash2,
} from 'lucide-react';

export default function ImovelParadigma_planejamento({
  formData,
  setFormData,
  onBack,
  onNext,
}) {
  const [tentativas, setTentativas] = useState(formData.logAgendamento || []);
  const [modalScript, setModalScript] = useState(null);
  const [alvoIdx, setAlvoIdx] = useState(0);
  const [showAddContato, setShowAddContato] = useState(false);
  const [searchTel, setSearchTel] = useState('');
  const [contatoEncontrado, setContatoEncontrado] = useState(null);
  const [novoNome, setNovoNome] = useState('');
  const [novoEmail, setNovoEmail] = useState('');
  const [novoCargo, setNovoCargo] = useState('');
  const [extraindo, setExtraindo] = useState(false);
  const [erroExtracao, setErroExtracao] = useState(null);
  const [editandoIdx, setEditandoIdx] = useState(null);

  const isResponsavelTecnico = formData.userRole === 'MASTER';

  const dadosPeticao = formData.dadosPeticao || null;
  const advogadosPeticao = dadosPeticao?.advogados || [];
  const processoPeticao = dadosPeticao?.processo || null;

  /* ============================================================
     CONTATOS — combina petição + cadastro manual
     - Se petição trouxe advogados → usa eles
     - Se formData.contatosPlanejamento existe → usa
     - Senão → array VAZIO (sem "Pendente" fake)
  ============================================================ */
  const [contatosAtuais, setContatosAtuais] = useState(() => {
    // Prioridade 1: contatos salvos no formData
    if (Array.isArray(formData.contatosPlanejamento) && formData.contatosPlanejamento.length > 0) {
      return formData.contatosPlanejamento;
    }
    // Prioridade 2: advogados extraídos da petição
    if (advogadosPeticao.length > 0) {
      return advogadosPeticao.map((a, idx) => ({
        id: `pet-${idx}-${Date.now()}`,
        nome: a.nome || 'Sem nome',
        cargo: a.representa === 'REU' ? 'Advogado do Réu' : 'Advogado / Patronos',
        tel: a.telefone || '',
        email: a.email || '',
        oab: a.oab || null,
        origem: 'PETICAO',
      }));
    }
    // Senão: vazio (sem placeholders)
    return [];
  });

  /* Persiste contatos no formData sempre que mudarem */
  useEffect(() => {
    setFormData((prev) => {
      if (JSON.stringify(prev.contatosPlanejamento) === JSON.stringify(contatosAtuais)) {
        return prev;
      }
      return { ...prev, contatosPlanejamento: contatosAtuais };
    });
  }, [contatosAtuais, setFormData]);

  /* Sincroniza quando petição é processada */
  useEffect(() => {
    if (advogadosPeticao.length === 0) return;

    setContatosAtuais((prev) => {
      // Não sobrescreve se o usuário já adicionou contatos manualmente
      const temManual = prev.some((c) => c.origem !== 'PETICAO');
      if (temManual) return prev;

      return advogadosPeticao.map((a, idx) => ({
        id: `pet-${idx}-${Date.now()}`,
        nome: a.nome || 'Sem nome',
        cargo: a.representa === 'REU' ? 'Advogado do Réu' : 'Advogado / Patronos',
        tel: a.telefone || '',
        email: a.email || '',
        oab: a.oab || null,
        origem: 'PETICAO',
      }));
    });
  }, [dadosPeticao, advogadosPeticao]);

  /* ============================================================
     OCR DA PETIÇÃO
  ============================================================ */
  const extrairDadosPeticao = async () => {
    setErroExtracao(null);

    const peticaoDoc = formData?.documentos?.peticao;
    const arquivo = peticaoDoc?.file;

    if (!arquivo) {
      setErroExtracao('Nenhuma petição anexada. Use o botão flutuante (canto inferior direito).');
      return;
    }

    setExtraindo(true);
    try {
      const fd = new FormData();
      fd.append('arquivo', arquivo);
      if (formData?.laudo_id) fd.append('laudo_uuid', formData.laudo_id);

      const resp = await fetch('/api/imovel/ocr_gemini.php', {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });

      const data = await resp.json();

      if (!data.success) {
        setErroExtracao(data.erro || 'Falha ao processar a petição.');
        return;
      }

      const dadosPeticaoNovos = {
        processo: data.processo || null,
        partes: data.partes || null,
        advogados: Array.isArray(data.advogados) ? data.advogados : [],
        tipo_documento: data.tipo_documento,
        confianca: data.confianca_global,
        extraidoEm: new Date().toISOString(),
      };

      setFormData((prev) => ({ ...prev, dadosPeticao: dadosPeticaoNovos }));

      if (dadosPeticaoNovos.advogados.length > 0) {
        setContatosAtuais(dadosPeticaoNovos.advogados.map((a, idx) => ({
          id: `pet-${idx}-${Date.now()}`,
          nome: a.nome || 'Sem nome',
          cargo: a.representa === 'REU' ? 'Advogado do Réu' : 'Advogado / Patronos',
          tel: a.telefone || '',
          email: a.email || '',
          oab: a.oab || null,
          origem: 'PETICAO',
        })));
      } else {
        setErroExtracao('A petição foi processada mas nenhum advogado foi identificado. Adicione manualmente pelo botão +.');
      }
    } catch (e) {
      setErroExtracao(e?.message || 'Erro ao processar petição.');
    } finally {
      setExtraindo(false);
    }
  };

  /* ============================================================
     CADASTRO MANUAL
  ============================================================ */
  const categoriasCargos = [
    { grupo: '1. Sujeitos Processuais', itens: ['Perito Judicial', 'Assistente Técnico', 'Advogado / Patronos', 'Advogado do Réu', 'Juiz (Cientificação)'] },
    { grupo: '2. Representação do Imóvel', itens: ['Proprietário', 'Possuidor / Ocupante', 'Locatário', 'Comodatário', 'Usufrutuário'] },
    { grupo: '3. Condomínio', itens: ['Síndico', 'Subsíndico', 'Administradora'] },
    { grupo: '4. Apoio (Sem poder de autorização)', itens: ['Zelador', 'Porteiro', 'Funcionário do Condomínio'] },
    { grupo: '5. Técnicos Locais', itens: ['Engenheiro / Arquiteto do Imóvel', 'Responsável Manutenção', 'Encarregado / Mestre de Obras'] },
    { grupo: '6. Autoridades', itens: ['Oficial de Justiça', 'Força Policial'] },
  ];

  const dbContatosGrupo = [
    { tel: '11999998888', nome: 'Dr. Alberto Ramos', cargo: 'Advogado / Patronos', email: 'alberto@ramos.adv.br', historico: { sucessos: 12, recusas: 0 } },
    { tel: '11988887777', nome: 'Eng. Ricardo Silva', cargo: 'Assistente Técnico', email: 'ricardo@eng.com.br', historico: { sucessos: 15, recusas: 1 } },
  ];

  const handleSearchTel = (val) => {
    setSearchTel(val);
    const telLimpo = val.replace(/\D/g, '');
    const find = dbContatosGrupo.find(c => c.tel === telLimpo);
    if (find) {
      setContatoEncontrado(find);
      setNovoNome(find.nome);
      setNovoCargo(find.cargo);
      setNovoEmail(find.email);
    } else {
      setContatoEncontrado(null);
    }
  };

  const resetFormContato = () => {
    setSearchTel('');
    setNovoNome('');
    setNovoCargo('');
    setNovoEmail('');
    setContatoEncontrado(null);
    setEditandoIdx(null);
  };

  const salvarContato = () => {
    if (!novoNome.trim() || !novoCargo) return;

    const contato = {
      id: editandoIdx != null ? contatosAtuais[editandoIdx].id : `man-${Date.now()}`,
      nome: novoNome.trim(),
      cargo: novoCargo,
      tel: searchTel,
      email: novoEmail,
      oab: contatoEncontrado?.oab || null,
      origem: 'MANUAL',
    };

    if (editandoIdx != null) {
      setContatosAtuais((prev) => prev.map((c, i) => (i === editandoIdx ? contato : c)));
    } else {
      setContatosAtuais((prev) => [...prev, contato]);
    }

    resetFormContato();
    setShowAddContato(false);
  };

  const editarContato = (idx) => {
    const c = contatosAtuais[idx];
    setSearchTel(c.tel || '');
    setNovoNome(c.nome || '');
    setNovoCargo(c.cargo || '');
    setNovoEmail(c.email || '');
    setEditandoIdx(idx);
    setShowAddContato(true);
  };

  const excluirContato = (idx) => {
    if (!window.confirm(`Remover "${contatosAtuais[idx].nome}"?`)) return;
    setContatosAtuais((prev) => prev.filter((_, i) => i !== idx));
  };

  /* ============================================================
     AÇÕES DE CONTATO
  ============================================================ */
  const scriptVoz = {
    falar: [
      'Identificar-se como auxiliar da justiça/perícia.',
      'Informar o número do processo e o objetivo (Vistoria).',
      'Confirmar data, hora e ponto de encontro.',
      'Solicitar que animais sejam isolados e luzes acesas.',
      'Informar que a vistoria é técnica e visual.',
    ],
    naofalar: [
      'Não emitir opinião sobre o valor do imóvel.',
      "Não comentar sobre quem 'tem razão' no processo.",
      'Não aceitar documentos sem registro oficial.',
      'Não discutir honorários ou prazos processuais.',
      'Não prometer resultados ou conclusões favoráveis.',
    ],
  };

  const iniciarProtocoloVoz = (contato) => {
    if (!contato.tel) { alert('Contato sem telefone.'); return; }
    const log = {
      id: Date.now(),
      data: new Date().toLocaleDateString('pt-BR'),
      hora: new Date().toLocaleTimeString('pt-BR'),
      meio: 'Telefone',
      status: `CHAMADA MONITORADA: ${contato.nome} (${contato.cargo})`,
    };
    const novoLog = [log, ...tentativas];
    setTentativas(novoLog);
    setFormData(prev => ({ ...prev, logAgendamento: novoLog }));

    if (!isResponsavelTecnico) setModalScript(contato);
    else window.location.href = `tel:${contato.tel.replace(/\D/g, '')}`;
  };

  const confirmarLigacaoScript = () => {
    const tel = modalScript.tel.replace(/\D/g, '');
    setModalScript(null);
    window.location.href = `tel:${tel}`;
  };

  const dispararMensagem = (meio, contato) => {
    if (meio === 'WhatsApp' && !contato.tel) { alert('Contato sem telefone.'); return; }
    if (meio === 'E-mail' && !contato.email) { alert('Contato sem e-mail.'); return; }

    const log = {
      id: Date.now(),
      data: new Date().toLocaleDateString('pt-BR'),
      hora: new Date().toLocaleTimeString('pt-BR'),
      meio,
      status: `CIENTIFICAÇÃO ENVIADA: ${contato.nome} (${contato.cargo})`,
    };
    const novoLog = [log, ...tentativas];
    setTentativas(novoLog);
    setFormData(prev => ({ ...prev, logAgendamento: novoLog }));

    if (meio === 'WhatsApp') {
      window.open(`https://api.whatsapp.com/send?phone=55${contato.tel.replace(/\D/g, '')}`);
    } else if (meio === 'E-mail') {
      window.location.href = `mailto:${contato.email}`;
    }
  };

  const inputBase =
    'w-full h-10 bg-slate-950 border border-white/10 rounded-xl px-3.5 font-bold text-white text-[12px] outline-none focus:border-amber-400 transition-all';

  return (
    <div className="w-full animate-in fade-in duration-500 relative space-y-4">

      {/* MODAL SCRIPT */}
      {modalScript && (
        <div className="fixed inset-0 z-[120] bg-slate-950/95 backdrop-blur-xl flex items-center justify-center p-6">
          <div className="bg-slate-900 w-full max-w-3xl rounded-3xl shadow-2xl overflow-hidden border border-amber-400/20">
            <div className="p-6 bg-slate-950 flex justify-between items-center border-b border-white/5">
              <div className="flex items-center gap-4">
                <div className="w-11 h-11 bg-amber-400 rounded-2xl flex items-center justify-center shadow-lg">
                  <Mic size={18} className="text-slate-950" />
                </div>
                <div>
                  <h3 className="text-white text-base font-black italic uppercase tracking-tight">Script de Blindagem Pericial</h3>
                  <p className="text-amber-400/70 text-[9px] font-bold uppercase tracking-[0.25em] mt-0.5">Protocolo de Comunicação Segura</p>
                </div>
              </div>
              <button onClick={() => setModalScript(null)} className="text-slate-500 hover:text-white transition-all">
                <X size={22} />
              </button>
            </div>

            <div className="p-6 grid grid-cols-2 gap-4 max-h-[60vh] overflow-y-auto">
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-emerald-500 mb-2">
                  <CheckCircle size={14} />
                  <span className="text-[10px] font-black uppercase italic tracking-widest">O que comunicar</span>
                </div>
                {scriptVoz.falar.map((item, i) => (
                  <div key={i} className="p-3 bg-emerald-500/5 rounded-xl border border-emerald-500/20">
                    <p className="text-[11px] font-bold text-emerald-200 leading-snug">{item}</p>
                  </div>
                ))}
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-red-500 mb-2">
                  <AlertTriangle size={14} />
                  <span className="text-[10px] font-black uppercase italic tracking-widest">Zonas de Risco</span>
                </div>
                {scriptVoz.naofalar.map((item, i) => (
                  <div key={i} className="p-3 bg-red-500/5 rounded-xl border border-red-500/20">
                    <p className="text-[11px] font-bold text-red-200 leading-snug">{item}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="p-4 bg-slate-950 border-t border-white/5 flex justify-center">
              <button
                onClick={confirmarLigacaoScript}
                className="bg-amber-400 text-slate-950 px-8 py-3 rounded-xl font-black uppercase text-[11px] italic flex items-center gap-3 hover:bg-white transition-all shadow-lg"
              >
                <Phone size={16} /> Efetuar Ligação Agora
              </button>
            </div>
          </div>
        </div>
      )}

      {/* HEADER BARRA */}
      <div className="bg-slate-900 rounded-2xl px-5 py-3.5 border border-white/5 shadow-lg flex items-center justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 bg-slate-950 rounded-xl flex items-center justify-center border border-white/10 shrink-0">
            <User size={16} className="text-amber-400" />
          </div>
          <div className="min-w-0">
            <p className="text-[8px] font-black text-slate-500 uppercase tracking-[0.3em] leading-none">
              Controle de Agendamento
            </p>
            <h2 className="text-base font-black italic uppercase tracking-tight text-white leading-tight mt-1 truncate">
              {formData.engenheiro || 'Marcus Ferraz'}
            </h2>
          </div>
        </div>
        <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest hidden lg:block">
          Ref: {formData.reportNumber || '—'}
        </span>
      </div>

      {/* CARD DE EXTRAÇÃO DA PETIÇÃO */}
      {!dadosPeticao && (
        <div className="bg-slate-900 p-5 rounded-2xl border border-amber-400/20 shadow-lg flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-400/10 rounded-xl flex items-center justify-center border border-amber-400/30 shrink-0">
              <FileText size={18} className="text-amber-400" />
            </div>
            <div>
              <p className="text-[10px] font-black text-white uppercase tracking-widest leading-none">
                Extrair dados da petição inicial
              </p>
              <p className="text-[8px] text-slate-500 font-bold uppercase tracking-widest mt-1">
                Nome do advogado, OAB, telefone, e-mail, partes do processo
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={extrairDadosPeticao}
            disabled={extraindo}
            className="px-5 py-3 rounded-xl bg-amber-400 text-slate-950 font-black text-[10px] uppercase tracking-widest hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 shrink-0"
          >
            {extraindo ? (
              <><Loader2 size={14} className="animate-spin" /> Extraindo…</>
            ) : (
              <><Sparkles size={14} /> Extrair agora</>
            )}
          </button>
        </div>
      )}

      {erroExtracao && (
        <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 flex gap-2">
          <AlertTriangle size={14} className="shrink-0" />
          <p className="text-[9px] font-black uppercase tracking-wide leading-relaxed">{erroExtracao}</p>
        </div>
      )}

      {/* CARD DE DADOS EXTRAÍDOS DA PETIÇÃO */}
      {dadosPeticao && (
        <div className="bg-slate-900 p-4 rounded-2xl border border-emerald-500/30 shadow-lg space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CheckCircle2 size={12} className="text-emerald-400" />
              <span className="text-[9px] font-black text-emerald-400 uppercase tracking-widest">
                Petição processada · {dadosPeticao.advogados?.length || 0} advogado(s)
              </span>
            </div>
            <button
              type="button"
              onClick={extrairDadosPeticao}
              disabled={extraindo}
              className="text-[8px] font-black text-slate-500 hover:text-amber-400 uppercase tracking-widest transition-all"
            >
              {extraindo ? 'Reprocessando…' : 'Reprocessar'}
            </button>
          </div>
          {processoPeticao && (
            <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[9px]">
              {processoPeticao.vara && (
                <p className="text-slate-500">Vara: <span className="text-white font-black">{processoPeticao.vara}</span></p>
              )}
              {processoPeticao.comarca && (
                <p className="text-slate-500">Comarca: <span className="text-white font-black">{processoPeticao.comarca}</span></p>
              )}
              {processoPeticao.valor_causa != null && (
                <p className="text-slate-500 col-span-2">Valor da causa: <span className="text-amber-400 font-black">R$ {processoPeticao.valor_causa.toLocaleString('pt-BR')}</span></p>
              )}
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-12 gap-4">

        {/* COLUNA 01: PARTES INTERESSADAS */}
        <div className="col-span-12 lg:col-span-4">
          <div className="bg-slate-900 p-5 rounded-2xl border border-white/5 shadow-xl space-y-4">
            <div className="flex justify-between items-center border-b border-white/5 pb-3">
              <h3 className="text-[11px] font-black uppercase text-white tracking-widest italic">
                Partes Interessadas ({contatosAtuais.length})
              </h3>
              <button
                onClick={() => { resetFormContato(); setShowAddContato(!showAddContato); }}
                className="w-8 h-8 bg-amber-400 text-slate-950 rounded-lg flex items-center justify-center hover:bg-white transition-all shadow-md"
              >
                {showAddContato ? <X size={14} /> : <Plus size={14} />}
              </button>
            </div>

            {showAddContato && (
              <div className="p-4 bg-slate-950 rounded-xl border border-white/5 space-y-2.5 animate-in slide-in-from-top-2">
                <p className="text-[8px] font-black text-amber-400 uppercase tracking-widest">
                  {editandoIdx != null ? 'Editar contato' : 'Novo contato'}
                </p>
                <div className="relative">
                  <Search size={12} className="absolute left-3 top-3.5 text-slate-500" />
                  <input
                    type="text"
                    value={searchTel}
                    onChange={(e) => handleSearchTel(e.target.value)}
                    placeholder="Telefone (Busca DB)"
                    className={inputBase + ' pl-8'}
                  />
                </div>
                <input type="text" value={novoNome} onChange={(e) => setNovoNome(e.target.value)} placeholder="Nome Completo" className={inputBase} />
                <input type="email" value={novoEmail} onChange={(e) => setNovoEmail(e.target.value)} placeholder="E-mail Formal" className={inputBase} />
                <select value={novoCargo} onChange={(e) => setNovoCargo(e.target.value)} className={inputBase + ' cursor-pointer'}>
                  <option value="" className="bg-slate-900">Selecionar Papel...</option>
                  {categoriasCargos.map(cat => (
                    <optgroup key={cat.grupo} label={cat.grupo}>
                      {cat.itens.map(cargo => <option key={cargo} value={cargo} className="bg-slate-900">{cargo}</option>)}
                    </optgroup>
                  ))}
                </select>
                <div className="flex gap-2">
                  <button
                    onClick={() => { resetFormContato(); setShowAddContato(false); }}
                    className="flex-1 bg-white/5 text-slate-400 py-2.5 rounded-xl font-black uppercase text-[9px] italic hover:bg-white/10 transition-all"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={salvarContato}
                    disabled={!novoNome.trim() || !novoCargo}
                    className="flex-1 bg-amber-400 text-slate-950 py-2.5 rounded-xl font-black uppercase text-[9px] italic hover:bg-white disabled:opacity-30 transition-all"
                  >
                    {editandoIdx != null ? 'Salvar' : 'Adicionar'}
                  </button>
                </div>
              </div>
            )}

            <div className="space-y-2 max-h-[440px] overflow-y-auto pr-1">
              {contatosAtuais.length === 0 && (
                <div className="flex flex-col items-center justify-center py-10 text-slate-700">
                  <Users size={28} className="mb-2 opacity-30" />
                  <p className="text-[9px] font-black uppercase tracking-widest text-center leading-relaxed">
                    Nenhum contato cadastrado.<br />
                    Clique em + para adicionar<br />
                    ou extraia da petição.
                  </p>
                </div>
              )}

              {contatosAtuais.map((cont, idx) => (
                <div
                  key={cont.id || idx}
                  className={`p-3.5 rounded-xl border transition-all ${
                    alvoIdx === idx ? 'border-amber-400/40 bg-slate-950' : 'border-white/5 bg-slate-950/40 opacity-80'
                  }`}
                >
                  <div onClick={() => setAlvoIdx(idx)} className="cursor-pointer mb-3">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <p className={`text-[8px] font-black uppercase italic ${alvoIdx === idx ? 'text-amber-400' : 'text-slate-500'}`}>
                        {cont.cargo}
                      </p>
                      <div className="flex items-center gap-1">
                        {cont.origem === 'PETICAO' && (
                          <span className="text-[7px] font-black uppercase text-emerald-500 bg-emerald-500/10 px-1.5 py-0.5 rounded">
                            Petição
                          </span>
                        )}
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); editarContato(idx); }}
                          className="w-6 h-6 rounded bg-white/5 text-slate-500 hover:text-amber-400 hover:bg-white/10 transition-all flex items-center justify-center"
                          title="Editar"
                        >
                          <Pencil size={10} />
                        </button>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); excluirContato(idx); }}
                          className="w-6 h-6 rounded bg-white/5 text-slate-500 hover:text-red-400 hover:bg-red-500/10 transition-all flex items-center justify-center"
                          title="Remover"
                        >
                          <Trash2 size={10} />
                        </button>
                      </div>
                    </div>
                    <p className="text-[13px] font-black text-white leading-none">{cont.nome}</p>
                    {cont.oab && (
                      <p className="text-[9px] font-bold text-slate-500 mt-1">OAB {cont.oab}</p>
                    )}
                    <p className="text-[10px] font-mono text-slate-500 mt-1">{cont.tel || '—'}</p>
                    {cont.email && <p className="text-[9px] text-slate-500 italic mt-0.5 truncate">{cont.email}</p>}
                  </div>

                  <div className="grid grid-cols-3 gap-2">
                    <button
                      onClick={() => iniciarProtocoloVoz(cont)}
                      disabled={!cont.tel}
                      className="flex flex-col items-center gap-1 py-2.5 bg-amber-400 text-slate-950 rounded-lg hover:bg-white transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <Phone size={13} /><span className="text-[7px] font-black uppercase italic">Chamada</span>
                    </button>
                    <button
                      onClick={() => dispararMensagem('WhatsApp', cont)}
                      disabled={!cont.tel}
                      className="flex flex-col items-center gap-1 py-2.5 bg-emerald-500/10 text-emerald-500 rounded-lg hover:bg-emerald-500 hover:text-white transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <MessageCircle size={13} /><span className="text-[7px] font-black uppercase italic">Whats</span>
                    </button>
                    <button
                      onClick={() => dispararMensagem('E-mail', cont)}
                      disabled={!cont.email}
                      className="flex flex-col items-center gap-1 py-2.5 bg-white/5 text-slate-400 rounded-lg hover:bg-white/10 hover:text-white transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <Mail size={13} /><span className="text-[7px] font-black uppercase italic">E-mail</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* COLUNA 02: RASTRO */}
        <div className="col-span-12 lg:col-span-5">
          <div className="bg-slate-900 p-5 rounded-2xl border border-white/5 shadow-xl flex flex-col h-full min-h-[440px]">
            <div className="flex items-center gap-3 mb-4 border-b border-white/5 pb-3">
              <div className="w-8 h-8 bg-slate-950 rounded-lg flex items-center justify-center border border-white/10">
                <HistoryLog className="text-amber-400" size={14} />
              </div>
              <h3 className="text-[11px] font-black uppercase italic text-white tracking-widest">Rastro de Diligência</h3>
            </div>

            <div className="flex-1 space-y-2 overflow-y-auto pr-1">
              {tentativas.length === 0 && (
                <div className="flex flex-col items-center justify-center py-12 text-slate-700">
                  <HistoryLog size={26} className="mb-2 opacity-30" />
                  <p className="text-[9px] font-black uppercase tracking-widest">Sem tentativas registradas</p>
                </div>
              )}
              {tentativas.map(log => (
                <div key={log.id} className="p-3 bg-slate-950 rounded-xl border border-white/5 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                      log.meio === 'Telefone' ? 'bg-amber-400 text-slate-950'
                      : log.meio === 'WhatsApp' ? 'bg-emerald-500/20 text-emerald-500'
                      : 'bg-white/5 text-slate-400'
                    }`}>
                      {log.meio === 'Telefone' ? <Phone size={12} />
                        : log.meio === 'WhatsApp' ? <MessageCircle size={12} />
                        : <Mail size={12} />}
                    </div>
                    <div>
                      <p className="text-[8px] font-black text-slate-500 uppercase mb-0.5">{log.data} • {log.hora}</p>
                      <p className="text-[10px] font-black text-white uppercase tracking-tight">{log.status}</p>
                    </div>
                  </div>
                  <CheckCircle size={12} className="text-emerald-500 opacity-50 shrink-0" />
                </div>
              ))}
            </div>

            <div className="mt-4 bg-slate-950 p-4 rounded-xl border border-white/5 flex justify-between items-center">
              <div className="flex items-center gap-3">
                <ShieldAlert className="text-amber-400" size={16} />
                <p className="text-[9px] text-white font-black uppercase italic tracking-widest">Salvaguarda Jurídica</p>
              </div>
              <div className="flex gap-2">
                <button className="w-8 h-8 bg-white/5 rounded-lg flex items-center justify-center hover:bg-white/10 transition-all">
                  <ScaleIcon size={13} className="text-amber-400" />
                </button>
                <button className="w-8 h-8 bg-white/5 rounded-lg flex items-center justify-center hover:bg-white/10 transition-all">
                  <FileSignature size={13} className="text-amber-400" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* COLUNA 03: STATUS E AVANÇO */}
        <div className="col-span-12 lg:col-span-3 flex flex-col gap-4">
          <div className="bg-slate-900 p-5 rounded-2xl border border-white/5 shadow-xl space-y-4">
            <h4 className="text-[9px] font-black text-slate-500 uppercase tracking-[0.3em] italic">Status Operacional</h4>
            <button
              onClick={() => setFormData(prev => ({ ...prev, acessoConfirmado: !prev.acessoConfirmado }))}
              className={`w-full p-5 rounded-xl border transition-all flex flex-col items-center gap-3 ${
                formData.acessoConfirmado
                  ? 'bg-emerald-500/10 border-emerald-500/40'
                  : 'bg-slate-950 border-white/5'
              }`}
            >
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                formData.acessoConfirmado ? 'bg-emerald-500 text-white' : 'bg-white/5 text-slate-600'
              }`}>
                <CheckCircle2 size={22} />
              </div>
              <span className={`text-[10px] font-black uppercase italic tracking-widest ${
                formData.acessoConfirmado ? 'text-emerald-500' : 'text-slate-500'
              }`}>
                Acesso Confirmado
              </span>
            </button>
          </div>

          <button
            onClick={onNext}
            className="w-full bg-amber-400 text-slate-950 p-6 rounded-2xl shadow-xl hover:bg-white transition-all flex items-center justify-between group"
          >
            <div className="text-left">
              <span className="text-[8px] font-black text-slate-950/60 uppercase italic tracking-[0.3em] mb-1 block leading-none">
                Protocolo Concluído
              </span>
              <p className="text-[16px] font-black italic uppercase tracking-tight">Vistoria Campo</p>
            </div>
            <div className="w-11 h-11 bg-slate-950/10 rounded-xl flex items-center justify-center group-hover:bg-slate-950 group-hover:text-amber-400 transition-all duration-300">
              <Camera size={20} />
            </div>
          </button>

          <div className="flex items-center justify-center gap-2 text-[8px] font-black uppercase tracking-[0.3em] text-slate-600 italic mt-auto pt-2">
            <ShieldCheck size={12} />
            <span>Integridade Total • 2026</span>
          </div>
        </div>
      </div>
    </div>
  );
}